import { useMemo } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { SubscriptionType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/subscription_backfill_enums.pb';
import { useTranslation } from '@frontend/i18n';
import { InlineNotification, Text } from '@frontend/design-system';
import { useBackfillStatus } from '../../hooks/use-backfill-status';

export const BackfillInternalBanner = ({
  locationIds,
  customStyles,
}: {
  locationIds?: string[];
  customStyles?: Interpolation<Theme>;
}) => {
  const { t } = useTranslation('call-intelligence');
  const { relevantLocationStatuses, isWetagEnabled, isMixedSubscriptionPlan, aggregatedBannerState } =
    useBackfillStatus(locationIds);

  const totalLocationsCount = locationIds?.length || 0;
  const backfillDays = aggregatedBannerState.aggregatedBackfillDays;

  const bannerData = useMemo(() => {
    if (!isWetagEnabled || !relevantLocationStatuses.length) {
      return null;
    }

    const trialBackfillLocations = relevantLocationStatuses.filter(
      (loc) => loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL
    );
    const trialBackfillLocationsCount = trialBackfillLocations.length;
    const trialBackfillBatch1Pending = trialBackfillLocations.filter((loc) => !loc.isBatch1Ready).length;
    const trialBackfillBatch2Pending = trialBackfillLocations.filter(
      (loc) => loc.isBatch1Ready && !loc.isBatch2Ready
    ).length;
    const trialBackfillPendingCount = trialBackfillBatch1Pending + trialBackfillBatch2Pending;

    const proBackfillLocations = relevantLocationStatuses.filter(
      (loc) => loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_PRO
    );
    const proBackfillLocationsCount = proBackfillLocations.length;
    const proBackfillBatch1Pending = proBackfillLocations.filter((loc) => !loc.isBatch1Ready).length;
    const proBackfillBatch2Pending = proBackfillLocations.filter(
      (loc) => loc.isBatch1Ready && !loc.isBatch2Ready
    ).length;
    const proBackfillPendingCount = proBackfillBatch1Pending + proBackfillBatch2Pending;

    const totalPendingCount = trialBackfillPendingCount + proBackfillPendingCount;

    if (totalPendingCount === 0) {
      return null;
    }

    // Mixed subscription plans
    if (isMixedSubscriptionPlan) {
      if (totalLocationsCount === relevantLocationStatuses.length) {
        return {
          text: t("We're still preparing insights from your previous calls. We'll notify you when they're ready."),
        };
      } else {
        return {
          text: t(
            "We're still preparing insights from your previous calls for {{count}} locations. We'll notify you when they're ready.",
            { count: totalPendingCount }
          ),
        };
      }
    }

    // Pro subscription plan
    if (aggregatedBannerState.aggregatedSubscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_PRO) {
      if (proBackfillBatch1Pending > 0) {
        return {
          text: t(
            "Your subscription is activated for {{count}} locations. Insights for these locations will appear on your dashboard when they're ready.",
            { count: proBackfillLocationsCount }
          ),
        };
      }

      if (proBackfillBatch2Pending > 0) {
        if (totalLocationsCount === proBackfillLocationsCount) {
          return {
            text: t(
              "We're still preparing insights from your previous calls for {{count}} locations. We'll notify you when they're ready.",
              { count: proBackfillBatch2Pending }
            ),
          };
        } else {
          return {
            text: t(
              "We're still preparing insights from the last {{backfillDays}} days of calls for your {{count}} newly subscribed locations.",
              { backfillDays, count: proBackfillBatch2Pending }
            ),
          };
        }
      }
    }

    // Trial subscription plan
    if (aggregatedBannerState.aggregatedSubscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL) {
      if (trialBackfillBatch1Pending > 0) {
        return {
          text: t(
            "Your free trial is activated for {{count}} locations. Insights for these locations will appear on your dashboard when they're ready.",
            { count: trialBackfillLocationsCount }
          ),
        };
      }

      if (trialBackfillBatch2Pending > 0) {
        if (totalLocationsCount === trialBackfillLocationsCount) {
          return {
            text: t(
              "We're still preparing insights from your last {{backfillDays}} days of calls. We'll notify you when they're ready.",
              { backfillDays }
            ),
          };
        } else {
          return {
            text: t(
              "We're still preparing insights from the last {{backfillDays}} days of calls for your {{count}} trial locations.",
              { backfillDays, count: trialBackfillBatch2Pending }
            ),
          };
        }
      }
    }

    return null;
  }, [
    aggregatedBannerState.aggregatedSubscriptionType,
    isWetagEnabled,
    backfillDays,
    relevantLocationStatuses,
    isMixedSubscriptionPlan,
    totalLocationsCount,
    t,
  ]);

  if (!bannerData) {
    return null;
  }

  return (
    <InlineNotification
      size='medium'
      status='premium'
      customStyles={customStyles}
      data-testid='backfill-internal-banner'
    >
      <Text size='medium'>{bannerData.text}</Text>
    </InlineNotification>
  );
};
